import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Grid,
  Typography,
  Button,
  Box,
  Slide,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Menu,
  MenuItem,
} from '@mui/material';
import { withTransaction } from '@elastic/apm-rum-react';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSelector } from 'react-redux';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import { debounce } from 'lodash';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import ReactMuiTableListView from '../../components/Common/TableComponent/TableComponent';
import ReactMuiTableColumnHeaderTextEllipsis from '../../components/Common/ReactMuiTableColumnHeaderTextEllipsis';
import DeleteDialog from '../../components/Common/DeleteDialog';
import { ReactTableCheckbox } from '../../components/Common/ReactTableCheckbox';
import AddUserModal from '../../components/AddUserModal';
import AddBulkUserDialog from '../../components/AddBulkUserDialog';
import useOrganization from '../../hooks/UseOrganization';
import useUserGroupPermission from '../../hooks/useUserGroupPermission';
import utils from '../../utils/Utils';
import SearchBox from '../../components/Common/SearchBox';
import ChangeStatusDialog from '../../components/Common/ChangeStatusDialog';
import {
  CommonTableCell,
  StatusCell,
} from '../../components/Common/CommonTableCell';

/**
 * @function UserSetting
 * @description UserSetting - To display the data of users (username, email, first-name, last-name and status)
 * @param {function} updateUser - Function to update user details.
 * @param {function} deteleUser - Function to delete a user.
 * @param {function} fetchUserDetails - Function to fetch user details.
 * @param {Array<Object>} userDetailsList - List of user details.
 * @param {function} createUser - Function to create a new user.
 * @param {function} getGroupList - Function to fetch group list.
 * @param {function} updateUserActions - Function to update multiple users.
 * @param {function} updateUserPassword - Function to update user password.
 * @returns {JSX} UserSetting UI
 */
const UserSetting = () => {
  const theme = useTheme();
  const { getLogin, getPassword, getGroupList, syncUsersToAllTheProduct } =
    useOrganization();
  const {
    updateUser,
    deteleUser,
    fetchUserDetails,
    createUser,
    updateUserActions,
  } = useUserGroupPermission();
  const orgId = useSelector((state) => state.common?.orgName);
  const subadmin = useSelector((state) => state.common.subAdminBoolean);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);
  const [open, setOpen] = useState(false);
  const [filteredUserList, setFilteredUserList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQueryText, setSearchQueryText] = useState('');
  const [currentUserDetail, setCurrentUserDetail] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [actionConfirmDialog, setActionConfirmDialog] = useState(false);
  const [action, setAction] = useState({});
  const [mode, setMode] = useState('create');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [toggle, setToggle] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [emailAsUsername, setEmailAsUserName] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState({});

  useEffect(() => {
    getLogin(orgId).then((resp) => {
      setEmailAsUserName(resp?.data?.email_as_username);
    });
    getPassword(orgId).then((resp) => {
      setPasswordPolicy(resp?.data);
    });
  }, [orgId]);

  const newSelectedUsers = useCallback((filteredUsers, prevSelectedUsers) => {
    return filteredUsers.reduce(
      (acc, user) => {
        if (user.isSelected) {
          if (!acc.some((selectedUser) => selectedUser.id === user.id)) {
            acc.push(user);
          }
        } else {
          return acc.filter((selectedUser) => selectedUser.id !== user.id);
        }
        return acc;
      },
      [...prevSelectedUsers]
    );
  }, []);

  const [showBulkUserDialog, setShowBulkUserDialog] = useState(false);

  /**
   * @function updateCheckboxStatusFor
   * @param {object} row - the row object contains all the data of the selected row
   * @description updateCheckboxStatusFor -  when the checkbox is clicked, this function filters the the selected users from the userlist and 
     adds them in setSelectedUsers state.
   * @returns {void} - updates the setSelectedUsers state
   */
  const updateCheckboxStatusFor = useCallback(
    (row) => {
      const rowIndex = Number.isInteger(row.index) ? row.index : undefined;

      if (
        typeof rowIndex !== 'number' ||
        !Number.isInteger(rowIndex) ||
        rowIndex < 0 ||
        rowIndex >= (filteredUserList?.data?.length || 0)
      ) {
        return;
      }

      setFilteredUserList((prev) => {
        const next = { ...prev, data: [...prev.data] };
        next.data[rowIndex] = {
          ...next.data[rowIndex],
          isSelected: !next.data[rowIndex].isSelected,
        };
        return next;
      });

      setSelectedUsers((prevSelected) => {
        const updatedData = [...filteredUserList.data];
        updatedData[rowIndex] = {
          ...updatedData[rowIndex],
          isSelected: !updatedData[rowIndex].isSelected,
        };
        return newSelectedUsers(updatedData, prevSelected);
      });
    },
    [filteredUserList, newSelectedUsers]
  );

  /**
   * @function selectHeader
   * @description selectHeader - this function renders the Select header in the table
   * @returns {JSX}
   */
  const selectHeader = () => {
    return (
      <Box display="flex" flexGrow="1" alignItems="left" flexDirection="column">
        <Typography variant="body3" fontWeight="800" color="inherit">
          Select
        </Typography>
      </Box>
    );
  };

  /**
   * @function cellForColsID
   * @description cellForColsID - this function renders the cell for checkbox
   * @returns {JSX}
   */
  const cellForColsID = ({ row }) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="100%"
      height="100%"
    >
      <ReactTableCheckbox
        {...row.getToggleRowSelectedProps()}
        checked={row?.original?.isSelected}
        onChange={() => updateCheckboxStatusFor(row)}
      />
    </Box>
  );

  const tableColumns = useMemo(() => {
    const columns = [
      {
        heading: 'Username',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'username',
        width: 200,
        Cell: CommonTableCell,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'Email',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'email',
        width: 200,
        Cell: CommonTableCell,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'First Name',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'given_name',
        width: 150,
        Cell: CommonTableCell,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'Last Name',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'family_name',
        width: 150,
        Cell: CommonTableCell,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'Status',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'is_active',
        Cell: StatusCell,
        sortType: utils.statusSort,
        width: 150,
      },
    ];

    // Add selectHeader column only if toggle is true
    return toggle
      ? [
          {
            Header: selectHeader,
            accessor: 'isSelected',
            width: 80,
            disableSortBy: true,
            isSorted: false,
            Cell: cellForColsID,
          },
          ...columns,
        ]
      : columns;
  }, [toggle, utils, selectHeader]);

  /**
   * @function fetchUsers
   * @param {number} pageIndex - The page page index of current page.
   * @param {number} pageSize - The page size of current page.
   * @param {number} searchTerm - search term for filtring the data.
   * @description fetchUsers - hook that returns a memoized callback function to fetch user details based on the organization ID.
   * @returns {void}
   */
  const fetchUsers = useCallback(
    ({
      pageIndex: _pageIndex,
      pageSize: _pageSize,
      searchTerm: _searchTerm,
    }) => {
      const body = {
        ...(_searchTerm?.length !== 0 && _pageIndex === 1
          ? {}
          : { page: _pageIndex }),
        page_size: _pageSize,
        search: _searchTerm,
      };
      if (orgId && _pageIndex >= 0 && _pageSize > 0) {
        return fetchUserDetails(body, orgId).then((res) => {
          const selectedUserIds = new Set(selectedUsers.map((el) => el.id));
          res.data = res?.data?.map((u) => ({
            ...u,
            isSelected: selectedUserIds.has(u.id),
          }));
          setFilteredUserList(res);
        });
      }
    },
    [orgId, pageIndex, pageSize, searchTerm, selectedUsers]
  );

  /**
   * @function fetchUsers
   * @param {number} pageIndex - The page page index of current page.
   * @param {number} pageSize - The page size of current page.
   * @param {number} searchTerm - search term for filtering the data.
   * @description fetchUsers - function returns a memoized callback function to fetch user details after the active/inactive action is performed.
   * @returns {void}
   */
  const fetchUsersAfterAction = useCallback(
    ({
      pageIndex: _pageIndex,
      pageSize: _pageSize,
      searchTerm: _searchTerm,
    }) => {
      const body = {
        ...(_searchTerm?.length !== 0 && _pageIndex === 1
          ? {}
          : { page: _pageIndex }),
        page_size: _pageSize,
        search: _searchTerm,
      };
      if (orgId && _pageIndex >= 0 && _pageSize > 0) {
        fetchUserDetails(body, orgId).then((res) => {
          setFilteredUserList(res);
        });
      }
    },
    [orgId, pageIndex, pageSize, searchTerm]
  );

  useEffect(() => {
    setIsLoading(true);
    fetchUsers({ pageIndex, pageSize, searchTerm }).then(() => {
      setIsLoading(false);
    });
  }, [orgId]);

  useEffect(() => {
    fetchUsers({ pageIndex, pageSize, searchTerm });
  }, [pageIndex, pageSize, orgId]);
  useEffect(() => {
    fetchUsers({ pageIndex: 1, pageSize, searchTerm });
  }, [searchTerm, orgId]);

  /**
   * @function handleActionSubmit
   * @description memoized callback function that handles the submission of user actions.
   *              Depending on the selected action and users, it updates user actions or shows a warning.
   * @returns {void}
   */
  const handleActionSubmit = useCallback(() => {
    const body = {
      action: action.value,
      user_id_list: [],
    };
    updateUserActions(body, orgId).then(() => {
      setSelectedUsers([]);
      fetchUsersAfterAction({ pageIndex, pageSize, searchTerm });
    });
  }, [
    updateUserActions,
    fetchUsers,
    selectedUsers,
    orgId,
    setSelectedUsers,
    pageIndex,
    pageSize,
    searchTerm,
    action,
  ]);

  const handleActionMultiSelect = useCallback(
    (actionValue) => {
      if (selectedUsers?.length > 0) {
        const body = {
          action: actionValue,
          user_id_list: selectedUsers.map((ele) => ele?.id),
        };
        updateUserActions(body, orgId).then(() => {
          setSelectedUsers([]);
          fetchUsersAfterAction({ pageIndex, pageSize, searchTerm });
          setToggle(false);
        });
      }
    },
    [
      selectedUsers,
      updateUserActions,
      orgId,
      fetchUsersAfterAction,
      pageIndex,
      pageSize,
      searchTerm,
    ]
  );

  /**
   * @function debouncedSave
   * @description debounce function to update the search term
   * @returns {void} sets the the searchTerm value
   * @example debouncedSave()
   */
  const debouncedSave = useCallback(
    debounce((data) => {
      setSearchTerm(data);
    }, 800),
    []
  );

  /**
   * @function searchUserByName
   * @description handles the search input change to filter applications by name.
   * @param {object} e - The event object from the search input field.
   * @returns {void} sets the the searchTerm value
   * @example searchUserByName(event)
   */
  const searchUserByName = useCallback(
    (e) => {
      debouncedSave(e.target.value);
    },
    [debouncedSave]
  );

  /**
   * @function handleSetSearchText
   * @description handles the input field of searchText.
   * @param {object} searchText - the current text is passed from user for searching.
   * @returns {void} sets the searchText value
   * @example handleSetSearchText(searchText)
   */
  const handleSetSearchText = useCallback((searchText) => {
    setSearchQueryText(searchText);
  }, []);

  /**
   * @function handleDeleteUser
   * @description handleDeleteUser - memoized callback function that handles the deletion of a user. It deletes the user based on the organization ID and user ID,
   * then fetches the updated list of users and closes the delete confirmation modal.
   * @param {string} orgName - The ID of the organization.
   * @param {number} id - The ID of the user to be deleted.
   * @returns {void}
   */
  const handleDeleteUser = useCallback(
    (orgName, id) => {
      deteleUser(orgName, id).then(() => {
        fetchUsers({ pageIndex, pageSize, searchTerm });
      });
      setShowDeleteModal(false);
    },
    [deteleUser, fetchUsers, pageIndex, pageSize, searchTerm]
  );

  /**
   * @function handleDisableUser
   * @description handleDisableUser - Handles disabling a user by updating their status. It calls the `updateUser` function
   * with the provided organization ID, user ID, and payload, then fetches the updated list of users.
   * @param {string} organizationID - The ID of the organization.
   * @param {number} id - The ID of the user to be disabled.
   * @param {Object} payload - The payload containing the update data for the user.
   * @returns {void}
   */
  const handleDisableUser = useCallback(
    (organizationID, userDetail) => {
      updateUser(organizationID, userDetail.id, {
        is_active: !userDetail.is_active,
        email: userDetail.email,
        given_name: userDetail.firstName,
        family_name: userDetail.lastName,
        password: userDetail.password,
        group_ids: userDetail.group_ids,
      }).then(() => {
        setShowStatusModal(false);
        fetchUsers({ pageIndex, pageSize, searchTerm });
      });
    },
    [
      updateUser,
      setShowStatusModal,
      fetchUsers,
      pageIndex,
      pageSize,
      searchTerm,
    ]
  );
  /**
   * @function onTableStateChangeMethod
   * @description helps in setting the pageIndex and pageSize
   * @param {object} pageIndex - pageIndex method of the table.
   * @returns {void} - updates the pageIndex and pageSize.
   * @example onTableStateChangeMethod(pageIndex)
   */
  const onTableStateChangeMethod = useCallback(
    ({ pageIndex: _pageIndex, pageSize: _pageSize }) => {
      setPageIndex(_pageIndex + 1);
      setPageSize(_pageSize);
    },
    [setPageIndex, setPageSize]
  );

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (handler) => {
    const syntheticEvent = {
      target: { closest: () => true },
      stopPropagation: () => {},
    };
    handler(syntheticEvent);
    handleClose();
  };

  /**
   *@constant menus
   * @description menus - A memoized array of menu items used for user actions. Each menu item includes a label, id, icon, and an onClick handler.
   * The onClick handlers perform various actions such as resetting passwords, editing, deleting, and changing the status of a user.
   * @type {Array<Object>}
   * @param {Function} setCurrentUserDetail - Function to set the current user detail.
   * @param {Function} setMode - Function to set the mode (e.g., "edit").
   * @param {Function} setOpen - Function to open the edit modal.
   * @param {Function} setShowDeleteModal - Function to show the delete confirmation modal.
   * @param {Function} handleDisableUser - Function to handle disabling a user.
   * @param {string} orgId - The ID of the organization.
   * @returns {Array<Object>} An array of menu items.
   */
  const menus = useMemo(
    () => [
      {
        label: 'Edit',
        id: 'edit',
        icon: EditIcon,
        isMenuDisabled: () => false,
        onClick: ({ props }) => {
          setCurrentUserDetail(props?.original);
          setMode('edit');
          setOpen(true);
        },
      },
      {
        label: 'Delete',
        id: 'delete',
        icon: DeleteIcon,
        isMenuDisabled: () => false,
        onClick: ({ props }) => {
          setShowDeleteModal(true);
          setCurrentUserDetail(props?.original);
        },
      },
      {
        label: 'Change Status',
        id: 'disable',
        icon: ChangeCircleIcon,
        isMenuDisabled: () => false,
        onClick: ({ props }) => {
          setCurrentUserDetail(props?.original);
          setShowStatusModal(true);
        },
      },
    ],
    [setCurrentUserDetail, pageIndex, pageSize, searchTerm, subadmin]
  );

  const handleMarkActive = useCallback(
    () => handleActionMultiSelect('mark_active'),
    [handleActionMultiSelect]
  );
  const handleMarkInactive = useCallback(
    () => handleActionMultiSelect('mark_inactive'),
    [handleActionMultiSelect]
  );

  const handleCreateUserClick = useCallback(() => setOpen(true), []);

  const handleToggleClick = useCallback(() => setToggle(true), []);

  const handleDeactivateAllClick = useCallback(() => {
    setActionConfirmDialog(true);
    setAction({
      value: 'mark_all_inactive',
      label: 'mark all user inactive',
    });
  }, []);

  const handleActivateAllClick = useCallback(() => {
    setActionConfirmDialog(true);
    setAction({
      value: 'mark_all_active',
      label: 'mark all user active',
    });
  }, []);

  const handleSearchInput = useCallback(
    (e) => handleSetSearchText(e.target.value),
    [handleSetSearchText]
  );

  const handleDialogClose = useCallback(
    () => setActionConfirmDialog(false),
    []
  );

  const handleStatusModalClose = useCallback(
    () => setShowStatusModal(false),
    []
  );

  const handleBulkDialogClose = useCallback(
    () => setShowBulkUserDialog(false),
    []
  );

  const handleDeleteModalClose = useCallback(
    () => setShowDeleteModal(false),
    []
  );

  const handleDialogCancel = useCallback(() => {
    setActionConfirmDialog(false);
    setToggle(true);
  }, []);

  const handleDialogSubmit = useCallback(() => {
    setActionConfirmDialog(false);
    handleActionSubmit();
  }, [handleActionSubmit]);

  const handleStatusChange = useCallback(
    () => handleDisableUser(orgId, currentUserDetail),
    [handleDisableUser, orgId, currentUserDetail]
  );

  const handleBulkUpload = useCallback(
    () => fetchUsers({ pageIndex, pageSize, searchTerm }),
    [fetchUsers, pageIndex, pageSize, searchTerm]
  );

  const handleDeleteConfirm = useCallback(
    () => handleDeleteUser(orgId, currentUserDetail?.id),
    [handleDeleteUser, orgId, currentUserDetail?.id]
  );

  const handleCancelOnClickMultiSelect = useCallback(() => {
    setToggle(false);
    setSelectedUsers([]);
    fetchUsersAfterAction({ pageIndex, pageSize, searchTerm });
  }, [fetchUsersAfterAction, pageIndex, pageSize, searchTerm]);

  const menuItems = [
    {
      label: 'Change Status',
      handler: handleToggleClick,
      title: 'Change Status',
    },
    {
      label: 'Deactivate All',
      handler: handleDeactivateAllClick,
      title: 'Deactivate All',
    },
    {
      label: 'Activate All',
      handler: handleActivateAllClick,
      title: 'Activate All',
    },
  ];

  return (
    <Grid item padding="20px">
      <Box display="flex" flexDirection="column" overflow="auto">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexDirection="row"
        >
          <Box width="200px">
            <Typography variant="h3">Users</Typography>
          </Box>

          <Box display="flex" flexGrow={1} flexDirection="row-reverse" gap={2}>
            <Button
              disabled={false}
              title={
                subadmin
                  ? 'You are not authorized to access this!'
                  : 'Create User'
              }
              variant="contained"
              onClick={handleCreateUserClick}
            >
              + Create User
            </Button>
            <Button
              disabled={false}
              title={
                subadmin
                  ? 'You are not authorized to access this!'
                  : 'Sync Users'
              }
              variant="outlined"
              onClick={() => {
                syncUsersToAllTheProduct(orgId);
              }}
            >
              Sync Users
            </Button>
          </Box>
        </Box>
        <Grid container mt={2}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between">
              {/* Reusable Search Box */}
              <Box width="200px">
                <SearchBox
                  placeholder="Search"
                  variant="filled"
                  value={searchQueryText}
                  onChange={searchUserByName}
                  onInput={handleSearchInput}
                  aria-label="Search user"
                />
              </Box>
              <Box
                display="flex"
                flexGrow={1}
                flexDirection="row-reverse"
                alignItems="center"
              >
                <Box display="flex" flexDirection="row" gap={2}>
                  {toggle && (
                    <Button
                      variant="outlined"
                      onClick={handleCancelOnClickMultiSelect}
                    >
                      Cancel
                    </Button>
                  )}
                  {!toggle && (
                    <>
                      <IconButton
                        aria-label="more"
                        aria-controls={openMenu ? 'card-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={openMenu ? 'true' : undefined}
                        onClick={handleClick}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        id="card-menu"
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        {menuItems.map((item) => (
                          <MenuItem
                            key={item.label}
                            onClick={() => handleMenuItemClick(item.handler)}
                            disabled={item.disabled}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              minWidth: '150px',
                              fontSize: '14px',
                              color: item.color || 'inherit',
                            }}
                          >
                            {item.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="50vh"
            >
              <Typography variant="h6" textAlign="center">
                Loading...
              </Typography>
            </Box>
          ) : (
            <Slide in={true} direction="up" mountOnEnter timeout={500}>
              {filteredUserList?.data?.length > 0 ? (
                <Box pt={2}>
                  <ReactMuiTableListView
                    key={toggle ? 'with-select' : 'without-select'}
                    rowLevelRightClickMenus={menus}
                    data={filteredUserList?.data}
                    columns={tableColumns}
                    onTableStateChange={onTableStateChangeMethod}
                    getHeaderProps={() => ({
                      style: {
                        display: 'flex',
                        alignItems: 'center',
                      },
                    })}
                    getRowProps={() => ({
                      style: {
                        position: 'relative',
                      },
                    })}
                    enableRowSelection={true}
                    toggle={toggle}
                    pageCount={Number(filteredUserList?.max_pages)}
                    dataLength={Number(filteredUserList?.total_count)}
                    enablePagination={true}
                    initialPageSize={5}
                    rowsPerPageOptions={[5, 10, 15, 20]}
                    enableManualFilter={false}
                    enableControlledPagination={true}
                    showPaginationAction={false}
                    skipPageReset={true}
                    searchTerm={searchTerm}
                  />
                </Box>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  overflow="auto"
                  minHeight="300px"
                  height="calc(100vh - 438px)"
                >
                  {searchTerm ? (
                    <NoDataImage
                      style={{ color: theme.palette.primary.main }}
                    />
                  ) : (
                    <NoDataCreated
                      style={{ color: theme.palette.primary.main }}
                    />
                  )}
                  <Typography
                    variant="subtitle1"
                    textAlign="center"
                    sx={{ pt: '10px' }}
                  >
                    {searchTerm
                      ? 'No matching result found.'
                      : 'No users found. Please create a user to proceed.'}
                  </Typography>
                </Box>
              )}
            </Slide>
          )}

          {toggle && selectedUsers.length > 0 && (
            <Box
              px={4}
              py={2}
              sx={{
                display: 'flex',
                position: 'absolute',
                bottom: '100px',
                left: '58%',
                transform: 'translateX(-50%)',
                borderRadius: '8px',
                backgroundColor: theme.palette.other?.multipleSnackBarBg,
                height: '60px',
                width: '671px',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2" color="white">
                {selectedUsers.length} User(s) selected to perform action
              </Typography>

              <Box>
                <Button
                  sx={{
                    marginRight: '16px',
                    borderColor: theme.palette.common.white,
                  }}
                  variant="outlined"
                  onClick={handleMarkActive}
                >
                  <Typography variant="body2" color="white">
                    Mark Active
                  </Typography>
                </Button>

                <Button
                  sx={{ borderColor: theme.palette.common.white }}
                  variant="outlined"
                  onClick={handleMarkInactive}
                >
                  <Typography variant="body2" color="white">
                    Mark Inactive
                  </Typography>
                </Button>

                <IconButton
                  sx={{
                    padding: '8px 16px',
                  }}
                  onClick={handleCancelOnClickMultiSelect}
                >
                  <Typography variant="body2" color="white" paddingLeft={1}>
                    Cancel
                  </Typography>
                </IconButton>
              </Box>
            </Box>
          )}
        </Grid>
        <ChangeStatusDialog
          open={showStatusModal}
          onCloseClick={handleStatusModalClose}
          onChangeClick={handleStatusChange}
          confirmationMsg={`Are you sure you want to change the status of "${currentUserDetail?.username}" to "${currentUserDetail?.is_active ? 'Inactive' : 'Active'}"?`}
        />
        {showBulkUserDialog && (
          <AddBulkUserDialog
            open={showBulkUserDialog}
            handleClose={handleBulkDialogClose}
            OnUploaded={handleBulkUpload}
          />
        )}

        <AddUserModal
          open={open}
          setOpen={setOpen}
          mode={mode}
          setMode={setMode}
          currentUserDetail={currentUserDetail}
          createUser={createUser}
          updateUser={updateUser}
          fetchUserDetails={fetchUsers}
          getGroupList={getGroupList}
          organizationID={orgId}
          pageIndex={pageIndex}
          pageSize={pageSize}
          searchTerm={searchTerm}
          emailAsUsername={emailAsUsername}
          passwordPolicy={passwordPolicy}
        />
        <Dialog
          fullWidth={true}
          maxWidth="xs"
          open={actionConfirmDialog}
          onClose={handleDialogClose}
        >
          <DialogTitle>Perform Action</DialogTitle>
          <IconButton
            id="add_result_to_mmd_close_btn"
            color="primary"
            aria-label="close"
            onClick={handleDialogClose}
            size="large"
            sx={{
              position: 'absolute',
              right: 16,
              top: 8,
            }}
          >
            <CloseIcon style={{ color: theme.palette.other.icon }} />
          </IconButton>
          <DialogContent dividers>
            <Typography variant="subtitle1">
              {`Are you sure want to ${action.label}?`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogCancel} variant="outlined">
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleDialogSubmit}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
        <DeleteDialog
          open={showDeleteModal}
          onCloseClick={handleDeleteModalClose}
          onDeleteClick={handleDeleteConfirm}
          confirmationMsg={`Are you sure you want to delete "${
            currentUserDetail?.given_name || currentUserDetail?.family_name
              ? `${currentUserDetail?.given_name || ''} ${currentUserDetail?.family_name || ''}`.trim()
              : currentUserDetail?.username
          }" user?`}
          resourceName="Users"
        />
      </Box>
    </Grid>
  );
};

UserSetting.propTypes = {
  updateUser: PropTypes.func.isRequired,
  deteleUser: PropTypes.func.isRequired,
  fetchUserDetails: PropTypes.func.isRequired,
  userDetailsList: PropTypes.arrayOf.isRequired,
  createUser: PropTypes.func.isRequired,
  getGroupList: PropTypes.func.isRequired,
  updateUserActions: PropTypes.func.isRequired,
  updateUserPassword: PropTypes.func.isRequired,
};

export default withTransaction('UserSetting', 'component')(UserSetting);
